/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from "react";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericTabs, { TabOption } from "@ui-components/Tabs/GenericTabs";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useAuth0 } from "@auth0/auth0-react";
import { getReflectorsList } from "../../request/request";
import { createCluster } from "../request/request";
import { useAppSelector } from "@shared/redux/hooks";
import ClusterStep1 from "./ClusterStep1";
import ClusterStep2 from "./ClusterStep2";
import ClusterStep3 from "./ClusterStep3";

export interface Reflector {
  id: string;
  name: string;
  data?: { region?: string };
  licenseId?: string;
  statusPeer?: boolean;
  clusterId?: number | null;
  cluster?: {
    id: number;
    name: string;
    description: string;
    enable: boolean;
  } | null;
}
export interface ClusterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const STEPS: TabOption[] = [
  { label: "Step 1", value: "0" },
  { label: "Step 2", value: "1" },
  { label: "Step 3", value: "2" },
];

const ClusterModal: React.FC<ClusterModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [step, setStep] = useState<string>("0");
  const [clusterName, setClusterName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [reflectors, setReflectors] = useState<Reflector[]>([]);
  const [selectedReflectors, setSelectedReflectors] = useState<Reflector[]>([]);
  const [roles, setRoles] = useState<Record<string, { role?: string; address?: string }>>({});
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'confirm'; message: string } | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector(state => state.selection);

  // Fetch reflectors for step 2
  useEffect(() => {
    if (step === "1" && reflectors.length === 0) {
      (async () => {
        const token = await getAccessTokenSilently();
        const res = await getReflectorsList(token, tenantId);
        setReflectors(res.data);
      })();
    }
  }, [step, getAccessTokenSilently, reflectors.length]);

  // Validation
  const canProceedStep1 = clusterName.trim() !== "";
  
  // Check if selected reflectors are in the same region
  const selectedReflectorRegions = selectedReflectors.map(r => r.data?.region).filter(Boolean);
  const allSameRegion = selectedReflectorRegions.length === 0 || 
    selectedReflectorRegions.every(region => region === selectedReflectorRegions[0]);
  
  const canProceedStep2 = selectedReflectors.length === 2 && allSameRegion;

  const handleNext = () => setStep((s) => (parseInt(s) + 1).toString());
  const handleBack = () => setStep((s) => (parseInt(s) - 1).toString());

  const handleSubmit = async () => {
    if (!clusterName || !description || selectedReflectors.length === 0) {
      console.error('Missing required data:', { clusterName, description, selectedReflectors });
      setAlert({ type: 'error', message: 'Missing required data. Please fill in all fields.' });
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      
      // Format the payload according to the Cluster interface
      const payload = {
        description: description,
        region: selectedReflectors[0]?.data?.region || '',
        cloud: 'aws', // Default to AWS since cloud property is not in the interface
        status: 'active',
        reflectors: selectedReflectors.map(reflector => ({
          reflectorId: Number(reflector.id),
          role: roles[reflector.id]?.role || 'secondary'
        }))
      };

      
      // Make the API call to create the cluster
      const response = await createCluster(token, payload);
      
      
      // Show success alert first
      setAlert({ type: 'success', message: 'Cluster created successfully!' });
      
      // Call the parent's onSubmit callback with the response data immediately
      onSubmit({
        clusterName,
        description,
        selectedReflectors,
        roles,
        clusterId: response.data.id, // Pass the created cluster ID
        response: response.data
      });
      
    } catch (error) {
      console.error('Error creating cluster:', error);
      
      // Show error alert first
      setAlert({ type: 'error', message: 'Failed to create cluster. Please try again.' });
    }
  };

  const handleCloseAlert = () => {
    setAlert(null);
    onClose(); // Close the modal when alert is dismissed
  };

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} size="lg" title="Create cluster">
      <div className="flex flex-col h-full">
        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-4 max-h-[70vh] w-full max-w-2xl mx-auto">
          <GenericTabs
            tabs={STEPS}
            activeTab={step}
            onTabChange={setStep}
          />
          {step === "0" && (
            <ClusterStep1
              clusterName={clusterName}
              setClusterName={setClusterName}
              description={description}
              setDescription={setDescription}
              onNext={canProceedStep1 ? handleNext : undefined}
              onCancel={onClose}
            />
          )}
          {step === "1" && (
            <ClusterStep2
              reflectors={reflectors}
              selectedReflectors={selectedReflectors}
              setSelectedReflectors={setSelectedReflectors}
              onNext={canProceedStep2 ? handleNext : undefined}
              onBack={handleBack}
              allSameRegion={allSameRegion}
              selectedReflectorRegions={selectedReflectorRegions}
            />
          )}
          {step === "2" && (
            <ClusterStep3
              clusterName={clusterName}
              description={description}
              selectedReflectors={selectedReflectors}
              roles={roles}
              setRoles={setRoles}
              onBack={handleBack}
              onSubmit={handleSubmit}
            />
          )}
        </div>
        
        {/* Fixed button area at bottom - only for step 2 */}
        {step === "1" && (
          <div className=" p-4 bg-semantic-bg-primary">
            <div className="flex justify-end gap-2 max-w-2xl mx-auto">
              <GenericButton label="Cancel" variant="secondary" onClick={handleBack} />
              <GenericButton label="Next" variant="brand" onClick={canProceedStep2 ? handleNext : undefined} />
            </div>
          </div>
        )}
      </div>
      
      {/* Alert for cluster creation status */}
      <GenericAlert
        isOpen={alert !== null}
        onClose={handleCloseAlert}
        onConfirm={handleCloseAlert}
        title={alert?.type === 'success' ? 'Success' : alert?.type === 'error' ? 'Error' : 'Info'}
        description={alert?.message || ''}
        variant={alert?.type}
        confirmLabel="OK"
        cancelLabel=""
      />
    </GenericModal>
  );
};

export default ClusterModal;
